import { FC, useState } from "react";
import { Grid, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import classNames from "classnames";
import styles from "@/components/appointmentDashboard/appointmentTreeItemSubjectInformationList.module.css";
import dayjs from "dayjs";
import UserEditModal from "@/components/userAdministration/userEditModal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import AppointmentContextAccordion from "@/components/appointmentDashboard/appointmentContextAccordion";

interface Props {
  appointment: any;
  handleNavigate?: any;
  handleNavigateToCompany?: any;
  onUserUpdated?: () => void;
  currentCustomer?: any;
  isAppointmentFormView?: boolean;
  detailsExpanded?: boolean;
  onDetailsExpandedChange?: (expanded: boolean) => void;
}

const AppointmentTreeItemSubjectInformationList: FC<Props> = ({
  appointment,
  handleNavigate,
  handleNavigateToCompany,
  onUserUpdated,
  currentCustomer,
  isAppointmentFormView = false,
  detailsExpanded = true,
  onDetailsExpandedChange,
}) => {
  const { t } = useTranslation();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const isUserDeleted = currentCustomer?.[0]?.is_deleted === true;

  const handleOpenEditModal = () => {
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const handleUserUpdated = () => {
    onUserUpdated?.();
  };

  const getAppointmentAddress = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (
      buildingInformation.street_house &&
      buildingInformation.city &&
      buildingInformation.zip
    ) {
      return (
        buildingInformation.street_house +
        ", " +
        buildingInformation.zip +
        " " +
        buildingInformation.city
      );
    } else {
      return "-";
    }
  };

  const getAppointmentContactName = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (buildingInformation.contact_name) {
      return buildingInformation?.contact_name;
    } else {
      return "-";
    }
  };
  const getAppointmentContactPhone = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (buildingInformation.contact_phone) {
      return buildingInformation?.contact_phone;
    } else {
      return "-";
    }
  };
  const getAppointmentBuilding = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (buildingInformation.name) {
      return buildingInformation.name;
    } else {
      return "-";
    }
  };
  const getAppointmentAdditionalInformation = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (buildingInformation.additional_information) {
      return buildingInformation.additional_information;
    } else {
      return "-";
    }
  };
  const getAppointmentParkingSituation = () => {
    const buildingInformation = appointment.building_information ?? {};

    if (buildingInformation.parking_situation) {
      return buildingInformation.parking_situation;
    } else {
      return "-";
    }
  };

  const customListItem = (props: any) => {
    return (
      <ListItem {...props} sx={{ padding: "0" }}>
        {props.children}
      </ListItem>
    );
  };

  const renderSubjectName = () => {
    const today = dayjs();
    const name = appointment.subject?.name;
    const sex = appointment.subject?.person?.sex;
    const birthDate = dayjs(appointment.subject?.person?.birth_date).format(
      "DD.MM.YYYY",
    );
    const birthForCalculation = dayjs(
      new Date(appointment.subject?.person?.birth_date),
    );

    const age = today.diff(birthForCalculation, "year") + " " + t("year_age");

    let result = name;
    if (!appointment.subject?.is_company) {
      if (sex) {
        let tempSex;
        switch (sex) {
          case "male":
            tempSex = t("male").toLowerCase();
            break;
          case "female":
            tempSex = t("female").toLowerCase();
            break;
          case "diverse":
            tempSex = t("diverse").toLowerCase();
            break;
        }
        result += `, ${tempSex}`;
      }
      if (birthDate) {
        result += `, ${birthDate} (${age})`;
      }
    }

    return <>{result}</>;
  };

  const renderCompanyItem = () => {
    let result = appointment.subject.company.name;

    if (!appointment.subject?.is_company) {
      result = (
        <Link
          className={styles.link}
          to="/subjectOverview"
          target="_blank"
          onClick={handleNavigateToCompany}
        >
          {appointment.subject?.company?.name}
        </Link>
      );
    }
    return <>{result}</>;
  };

  const subjectIdentity = (
    <ListItemText
      primary={
        <div className={styles.subjectNameContainer}>
          {appointment.subject?.is_company
            ? t("company")
            : t("test_subject")}
          {!appointment.subject?.is_company &&
            appointment.subject?.id && (
              <Tooltip
                title={
                  isUserDeleted
                    ? t("is_deleted")
                    : t("edit_employee_information")
                }
                placement="right"
                arrow
              >
                <span className={styles.editButtonWrapper}>
                  <GenericButton
                    size="small"
                    variant="icon"
                    aria-label={t("edit_employee_information")}
                    onClick={handleOpenEditModal}
                    disabled={isUserDeleted}
                    className={classNames(styles.editButton, {
                      [styles.editButtonDeleted]: isUserDeleted,
                    })}
                  >
                    <EditIcon fontSize="small" />
                  </GenericButton>
                </span>
              </Tooltip>
            )}
        </div>
      }
      secondary={
        <Link
          className={classNames(styles.link, {
            [styles.linkDeleted]: isUserDeleted,
          })}
          to="/subjectOverview"
          onClick={handleNavigate}
          target="_blank"
        >
          {renderSubjectName()}
        </Link>
      }
      primaryTypographyProps={{ className: styles.primaryText }}
      secondaryTypographyProps={{ className: styles.secondaryText }}
    />
  );
  const building = appointment.building_information ?? {};
  const meaningfulValue = (value: unknown) =>
    typeof value === "string" && value.trim() && value.trim() !== "-"
      ? value.trim()
      : "";
  const cityAndZip = [building.zip, building.city].map(meaningfulValue).filter(Boolean).join(" ");
  const address = [meaningfulValue(building.street_house), cityAndZip].filter(Boolean).join(", ");
  const contact = [meaningfulValue(building.contact_name), meaningfulValue(building.contact_phone)]
    .filter(Boolean).join(" · ");
  const optionalFields = [
    { label: "building_location", value: address },
    { label: "building_contact_name", value: contact },
    ...(!appointment.subject?.is_company ? [
      { label: "email", value: meaningfulValue(appointment.subject?.email), link: true },
    ] : []),
    { label: "building_parking_situation", value: meaningfulValue(building.parking_situation) },
    { label: "building_additional_information", value: meaningfulValue(building.additional_information) },
  ];
  const populatedFields = optionalFields.filter((field) => field.value);

  return (
    <>
      {isAppointmentFormView ? (
        <AppointmentContextAccordion title={t("appointment_details")} expanded={detailsExpanded} onChange={(expanded) => onDetailsExpandedChange?.(expanded)}>
          <div className={styles.appointmentFormContainer}>
            <div className={styles.identity}>{subjectIdentity}</div>
            {!appointment.subject?.is_company && meaningfulValue(appointment.subject?.company?.name) && (
              <ListItemText primary={t("company")} secondary={renderCompanyItem()}
                primaryTypographyProps={{ className: styles.primaryText }}
                secondaryTypographyProps={{ className: styles.secondaryText }} />
            )}
            {meaningfulValue(building.name) && (
              <ListItemText primary={t("buildings")} secondary={building.name}
                primaryTypographyProps={{ className: styles.primaryText }}
                secondaryTypographyProps={{ className: styles.secondaryText }} />
            )}
            {populatedFields.map((field) => (
              <dl key={field.label} className={styles.appointmentFormField}>
                <dt className={styles.primaryText}>{t(field.label)}</dt>
                <dd>{field.link ? <a className={styles.link} href={`mailto:${field.value}`}>{field.value}</a> : field.value}</dd>
              </dl>
            ))}
          </div>
        </AppointmentContextAccordion>
      ) : (
      <Grid
        item
        container
        xs={12}
        component={List}
        className={styles.container}
      >
        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          {subjectIdentity}
        </Grid>
        {!appointment.subject?.is_company && (
          <Grid xs={12} sm={6} lg={4} item component={customListItem}>
            <ListItemText
              primary={t("email")}
              secondary={
                <a
                  className={styles.link}
                  href={`mailto:${appointment.subject?.email}`}
                >
                  {appointment.subject?.email}
                </a>
              }
              primaryTypographyProps={{ className: styles.primaryText }}
              secondaryTypographyProps={{ className: styles.secondaryText }}
            />
          </Grid>
        )}
        {!appointment.subject?.is_company && (
          <Grid xs={12} sm={6} lg={4} item component={customListItem}>
            <ListItemText
              primary={t("company")}
              secondary={renderCompanyItem()}
              primaryTypographyProps={{ className: styles.primaryText }}
              secondaryTypographyProps={{ className: styles.secondaryText }}
            />
          </Grid>
        )}
        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            primary={t("building_location")}
            secondary={getAppointmentAddress()}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryText }}
          />
        </Grid>

        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            secondary={getAppointmentBuilding()}
            primary={t("buildings")}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryText }}
          />
        </Grid>

        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            secondary={getAppointmentContactName()}
            primary={t("building_contact_name")}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryText }}
          />
        </Grid>
        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            secondary={getAppointmentContactPhone()}
            primary={t("building_contact_phone")}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryText }}
          />
        </Grid>

        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            secondary={getAppointmentAdditionalInformation()}
            primary={t("building_additional_information")}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryTextNoBold }}
          />
        </Grid>

        <Grid xs={12} sm={6} lg={4} item component={customListItem}>
          <ListItemText
            secondary={getAppointmentParkingSituation()}
            primary={t("building_parking_situation")}
            primaryTypographyProps={{ className: styles.primaryText }}
            secondaryTypographyProps={{ className: styles.secondaryTextNoBold }}
          />
        </Grid>
      </Grid>

      )}

      {!appointment.subject?.is_company && appointment.subject?.id && (
        <UserEditModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          userSubjectId={appointment.subject.id}
          companySubjectId={appointment.subject?.company?.subject_id}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </>
  );
};

export default AppointmentTreeItemSubjectInformationList;
